<script lang="ts">
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import beansproutLogo from '$lib/assets/beansprout-logo.svg';
	import courseFeature from '$lib/assets/course-landing/course-feature.jpg';
	import module1 from '$lib/assets/course-landing/module-1.webp';
	import module2 from '$lib/assets/course-landing/module-2.webp';
	import module3 from '$lib/assets/course-landing/module-3.webp';
	import module4 from '$lib/assets/course-landing/module-4.webp';
	import module5 from '$lib/assets/course-landing/module-5.webp';
	import module6 from '$lib/assets/course-landing/module-6.webp';
	import channelNewsAsia from '$lib/assets/external/channel-news-asia.webp';
	import lianhezaobao from '$lib/assets/external/lianhezaobao.webp';
	import sgx from '$lib/assets/external/sgx.png';
	import straitsTimes from '$lib/assets/external/straits-times.svg';
	import theBusinessTimes from '$lib/assets/external/the-business-times.webp';
	import yahooFinance from '$lib/assets/external/yahoo-finance.webp';
	import founderPortrait from '$lib/assets/home/founder-portrait.webp';
	import Button from '$lib/Button.svelte';
	import Container from '$lib/Container.svelte';
	import { ChartLine, ChartPie, Target } from 'lucide-svelte';
	import Checkmark from './Checkmark.svelte';
	import CourseModule from './CourseModule.svelte';
	import theEdge from '$lib/assets/external/the-edge.webp';

	beforeNavigate(() => {
		document.documentElement.style.scrollBehavior = 'auto';
	});
	afterNavigate(() => {
		document.documentElement.style.scrollBehavior = 'smooth';
	});
</script>

<div class="sticky top-0 z-30 w-full">
	<header class="border-b-gray-light bg-white-pure relative border-b-2">
		<div class="page-wrapper">
			<div class="mx-auto flex h-14 max-w-screen-xl items-center justify-between">
				<a href="/" class="mr-8 block shrink-0">
					<img src={beansproutLogo} alt="Beansprout logo" class="aspect-[175/37] w-32 lg:w-40" />
				</a>
				<Button to="#join-the-waitlist">Join the waitlist</Button>
			</div>
		</div>
	</header>
</div>

<section
	class="page-wrapper section-mb p-8 lg:p-16"
	style="background: linear-gradient(294deg, rgba(0, 168, 125, 0.3) 0%, rgba(255, 255, 255, 0.5) 100%);"
>
	<div class="mx-auto flex max-w-screen-xl flex-col justify-between gap-16 lg:flex-row">
		<div>
			<h1 class="max-serif mb-5 lg:mb-8">
				Start Investing with Confidence to <span class="italic">Grow Your Wealth</span>
			</h1>
			<p class="h2 mb-8 lg:mb-12">
				Because growing your wealth isn’t just about saving more — it’s about making every dollar
				work smarter.
			</p>

			<Button to="#join-the-waitlist" class="inline-block">Join the waitlist now</Button>
		</div>
		<div
			style="mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1.0) 87%, transparent 100%);"
			class="shrink-0 rounded-4xl lg:basis-[30rem]"
		>
			<img
				src={courseFeature}
				alt="Course feature"
				class="aspect-square w-full rounded-4xl object-cover"
			/>
		</div>
	</div>
</section>

<Container>
	<section class="section-mb">
		<h2 class="heading mb-6">Does this sound like you?</h2>
		<div class="mb-6 grid gap-6 lg:grid-flow-col">
			<div class="border-green-dark text-green-dark rounded-xl border-1 p-5">
				“I've sunk hours into research—articles, forums, you name it—but my SGD 200K portfolio still
				isn't delivering the steady income I need. It's exhausting.”
			</div>
			<div class="border-green-dark text-green-dark rounded-xl border-1 p-5">
				“Every stock or REIT pick feels like a gamble. I buy in, the price dips, and I'm left
				wondering if I've just thrown my money away."
			</div>
		</div>
		<p>
			You've worked tirelessly to build your portfolio—maybe it's SGD 100K, SGD 500K, or more.
			You've spent <span class="body-bold"
				>countless hours poring over articles, chasing conflicting advice, and second-guessing every
				decision.</span
			>
		</p>
		<br />
		<p>
			You buy a stock, convinced it's the right move, <span class="body-bold"
				>only to watch the price drop further, leaving you doubting everything.</span
			>
		</p>
		<br />
		<p>
			Here's the harsh reality: those vague strategies and endless guesswork could be eroding your
			hard-earned wealth, costing you the steady income you've dreamed of.
		</p>
		<br />
		<p>
			Don't let it slip away. <span class="body-bold"
				>Join the waitlist for our step-by-step course to grow your wealth, generate income, and
				retire happy.</span
			>
		</p>
	</section>
	<section class="section-mb">
		<h2 class="heading mb-8">What You’ll Learn:</h2>
		<div class="grid gap-10 lg:grid-cols-3">
			<div>
				<ChartPie strokeWidth={1} class="text-green-primary mb-6 h-10 w-10" />
				<p class="h2">Build a smart income-generating portfolio</p>
			</div>
			<div>
				<ChartLine strokeWidth={1} class="text-green-primary mb-6 h-10 w-10" />
				<p class="h2">Analyse stocks like a pro</p>
			</div>
			<div>
				<Target strokeWidth={1} class="text-green-primary mb-6 h-10 w-10" />
				<p class="h2">Retire with purpose, not just a number</p>
			</div>
		</div>
	</section>
</Container>

<section class="page-wrapper section-mb py-16" style="background-color: rgba(0, 168, 125, 0.04)">
	<div class="mx-auto max-w-screen-xl">
		<p class="card-regular text-gray-mid mb-3">WHY BEANSPROUT ACADEMY</p>
		<h2 class="heading mb-6">Designed to Help Grow Your Wealth</h2>
	</div>
	<ul class="mx-auto grid max-w-screen-xl gap-6">
		<li class="h2 flex items-start gap-3">
			<Checkmark />
			Focused on income and wealth building
		</li>
		<li class="h2 flex items-start gap-3">
			<Checkmark />
			No hype, no fluff — just timeless investing strategies
		</li>
		<li class="h2 flex items-start gap-3">
			<Checkmark />
			After-course support and private community
		</li>
	</ul>
</section>

<Container wrapperClass="section-mb">
	<p class="card-regular text-gray-mid mb-3 text-center">COURSE OVERVIEW</p>
	<h2 class="heading mx-auto mb-6 max-w-screen-lg text-center">
		The step-by-step course to grow your wealth, generate income, and retire happy.
	</h2>

	<p class="h3-regular mb-8 text-center">🧭 6 Modules to Help You Gain Confidence In Investing</p>

	<div class="mx-auto mb-16 grid max-w-screen-md gap-10">
		<CourseModule
			index={1}
			title="Foundations of Smart Investing (Investing 101)"
			objective="Build a strong financial foundation for retirement investing."
			image={module1}
			topics={[
				'Understanding Singapore’s Retirement Landscape (CPF, SRS, etc.)',
				'How Much You Need to Retire in Singapore',
				'Investing Basics: Compounding, Inflation, and Wealth Growth',
				'Risk vs. Reward: Finding Your Investment Style',
				'Common Investing Mistakes & How to Avoid Them'
			]}
			outcome="Gain clarity on retirement planning and set your financial goals."
		/>
		<CourseModule
			index={2}
			title="Fundamentals of Stock Analysis (Stock investing 101)"
			objective="Learn how to analyze stocks for long-term investing."
			image={module2}
			topics={[
				'Introduction to Stock Investing',
				'Financial Statement Analysis',
				'Valuation Metrics & How to Use Them',
				'Qualitative Analysis: Evaluating Business Models',
				'Case Study: Analyzing a Singapore Blue-Chip Stock'
			]}
			outcome="Gain the skills to evaluate stocks and make informed investment decisions."
		/>
		<CourseModule
			index={3}
			title="Building a Solid Investment Portfolio (Portfolio construction)"
			objective="Learn how to construct a well-balanced portfolio for long-term growth."
			image={module3}
			topics={[
				'Portfolio Construction: Core-Satellite Strategy',
				'Stocks, Bonds, REITs, ETFs – Different asset classes?',
				'Asset Allocation for Different Life Stages',
				'Risk management',
				'How to Manage & Rebalance Your Portfolio'
			]}
			outcome="Build a diversified portfolio suited for retirement."
		/>
		<CourseModule
			index={4}
			title="Passive Income Strategies for Retirement (Generating passive income)"
			objective="Generate sustainable income streams to support your retirement lifestyle."
			image={module4}
			topics={[
				'Dividend Investing: How to Pick High-Quality Dividend Stocks',
				'REITs: Passive Income Through Real Estate',
				'Bonds & Bond ETFs: Stability in Retirement',
				'CPF LIFE and Annuities: Lifelong Income Guarantees',
				'Creating a Passive Income Plan for Retirement'
			]}
			outcome="Build a steady stream of passive income for financial independence."
		/>
		<CourseModule
			index={5}
			title="Building a Wealth Mindset – Exploring Wealth Beyond Finances (Wellness and Mindset) "
			objective="Shift from just “retiring rich” to “retiring well.”"
			image={module5}
			topics={[
				'The meaning of true wealth: Time, purpose, relationships, health',
				'Financial freedom vs. financial independence',
				'Reframing retirement as a new beginning, not an end',
				'Lifestyle design: How to align money with what truly matters',
				'Giving back: Legacy planning and impact investing',
				'Case studies: Singaporeans who built wealth with purpose'
			]}
			outcome="Cultivate a mindset that sees wealth as more than money — preparing for a fulfilling and balanced retirement."
		/>
		<CourseModule
			index={6}
			title="Creating Your Retirement Financial Plan (Financial planning beyond investments)"
			objective="Develop a personalised action plan to achieve your retirement goals."
			image={module6}
			topics={[
				'How to Calculate Your Retirement Number',
				'Setting Financial Goals & Tracking Progress',
				'Withdrawal Strategies: Making Your Money Last',
				'Next Steps: Choosing the Right Investment Platforms',
				'Continuous Learning: How to Stay Updated with Market Trends'
			]}
			outcome="Walk away with a step-by-step roadmap to secure your retirement."
		/>
	</div>
	<div class="flex justify-center">
		<Button to="#join-the-waitlist" class="inline-block">Join the waitlist now</Button>
	</div>
</Container>

<section class="page-wrapper bg-[#F2FBF8] p-16">
	<div class="mx-auto max-w-screen-md">
		<p class="card-regular mb-3 text-center">TRAINER</p>
		<h2 class="heading mb-10 text-center">Who will you learn from?</h2>
		<div
			class="mb-10 flex flex-col-reverse items-center rounded-2xl bg-[linear-gradient(to_top,_#00a87d_-11%,_#0e5531_64%,_rgba(14,_55,_24,_0.99)_71%,_#000_84%)] lg:flex-row
			lg:items-end
			lg:bg-[linear-gradient(to_right,_#00a87d_-11%,_#0e5531_64%,_rgba(14,_55,_24,_0.99)_71%,_#000_84%)]"
		>
			<img
				src={founderPortrait}
				alt="Founder"
				class="object-cover object-top lg:block"
				style="width: 510px; height: 420px;"
			/>
			<div class="py-16 text-center">
				<p class="h1-serif text-white-pure">Gerald Wong</p>
				<hr class="border-green-primary mx-auto inline-block w-14 border-1" />
				<p class="h3 text-white-pure">Founder and CEO of Beansprout</p>
			</div>
		</div>
		<p>
			Gerald Wong, CFA, has more than 13 years of experience in investment advisory, and was most
			recently the Head of Singapore Securities Research at Credit Suisse. He was ranked top
			Singapore research analyst by Institutional Investor every year between 2016 to 2020. Gerald
			believes in helping retail investors achieve financial wellness by providing curated
			investment content and education programs, and serves on the CFA Society Singapore university
			outreach committee.
		</p>
	</div>
</section>

<Container wrapperClass="bg-green-dark py-8">
	<p class="heading text-white-pure text-center">
		Start taking action towards <span class="text-[#80ffc6]">GROWING YOUR WEALTH</span>
	</p>
</Container>

<section id="join-the-waitlist" class="page-wrapper section-mb scroll-target bg-[#F2FBF8] py-14">
	<form
		action="https://growbeansprout.us6.list-manage.com/subscribe/post?u=b1e286f7d69da07af7f83751a&amp;id=28d23bffd6&amp;f_id=00f401e3f0"
		method="post"
		name="mc-embedded-subscribe-form"
		class="mx-auto max-w-screen-md"
		data-type="email-subscription"
		data-content="email-subscription-course-waitlist"
		target="_self"
		novalidate
	>
		<header class="h1 mb-12 text-center">
			Limited–Time Offer: <br />
			Join the waitlist for to secure an Early Bird Pricing
		</header>
		<div class="flex flex-col gap-2 @lg:flex-row @lg:gap-1">
			<input
				type="email"
				value=""
				name="EMAIL"
				placeholder="Your email here."
				class="border-gray-light block h-12 w-full rounded-3xl border-1 p-4"
			/>

			<button
				class="btn btn-primary block w-full"
				type="submit"
				name="subscribe"
				data-type="email-subscription"
				data-content="email-subscription-course-waitlist"
				>Join the waitlist to secure early bird pricing</button
			>
		</div>
		<input type="hidden" name="tags" value="4058487" />
		<div aria-hidden="true" style="position: absolute; left: -5000px;">
			<input type="text" name="b_b1e286f7d69da07af7f83751a_28d23bffd6" tabindex="-1" value="" />
		</div>
	</form>
</section>

<Container wrapperClass="section-mb">
	<p class="card-regular text-gray-mid mb-3">TESTIMONIALS</p>
	<h2 class="heading mb-6">Why Investors Trust Beansprout</h2>
	<ul class="grid gap-6 lg:grid-cols-3">
		<li class="testimonial">
			<div class="border-l-green-primary mb-4 border-l-4 pl-4">
				<p class="text-green-dark mb-5 italic">
					I used what I learned to boost my income by S$5k/year – worth every cent.
				</p>
				<p class="text-gray-mid card-regular">— J. Tan</p>
			</div>
		</li>
		<li class="testimonial">
			<div class="border-l-green-primary mb-4 border-l-4 pl-4">
				<p class="text-green-dark mb-5 italic">
					Finally a course for Singaporeans that’s not just about hype stocks.
				</p>
				<p class="text-gray-mid card-regular">— M. Lim</p>
			</div>
		</li>
		<li class="testimonial">
			<div class="border-l-green-primary mb-4 border-l-4 pl-4">
				<p class="text-green-dark mb-5 italic">
					The most valuable takeaway is the importance of ARR and the rule of 40 in SaaS valuation,
					along with how AI, security, and subscriptions drive growth and revenue predictability.
				</p>
				<p class="text-gray-mid card-regular">— Webinar Participant</p>
			</div>
		</li>
	</ul>
</Container>

<Container wrapperClass="section-mb py-8">
	<h2 class="h1 mb-3 text-center">As seen on</h2>
	<div class="grid grid-cols-2 gap-5 lg:grid-cols-4 lg:grid-rows-3 [&_*]:py-5">
		<div
			class="border-gray-faint col-span-2 row-span-3 flex flex-col items-center justify-center max-lg:border-b-2 lg:col-start-2 lg:border-x-2"
		>
			<p class="h2 text-green-dark text-center italic">SGX Orb Awards 2024 Nominee</p>
			<img src={sgx} alt="SGX" class="mx-auto" />
		</div>
		<div class="lg:col-start-1 lg:row-start-1">
			<img src={yahooFinance} alt="Yahoo Finance" class="mx-auto w-40" />
		</div>
		<div>
			<img src={theEdge} alt="The Edge" class="mx-auto w-40" />
		</div>
		<div>
			<img src={straitsTimes} alt="The Straits Times" class="mx-auto w-40" />
		</div>
		<div>
			<img src={channelNewsAsia} alt="Channel News Asia" class="mx-auto w-40" />
		</div>
		<div>
			<img src={theBusinessTimes} alt="The Business Times" class="mx-auto w-40" />
		</div>

		<div>
			<img src={lianhezaobao} alt="Lianhe Zaobao" class="mx-auto h-20" />
		</div>
	</div>
</Container>

<style lang="postcss">
	@reference '../../app.css';

	.heading {
		font-size: 2rem;
		line-height: 2.5rem;
		font-weight: 500;

		@variant lg {
			font-size: 2.5rem;
			line-height: 3rem;
		}
	}

	.testimonial {
		background-color: var(--color-white-pure);
		border-radius: var(--radius-2xl);
		padding: 1.5rem;
		box-shadow:
			0 1px 1px 0 rgb(0 0 0 / 8%),
			0 1px 3px 1px rgba(0, 0, 0, 0.16);
	}
</style>
